<template>
  <header class="app-header" :class="{ 'app-header--scrolled': scrolled }">
    <nav class="navbar navbar-expand-lg">
      <router-link class="navbar-brand" to="/">
        <span class="brand-mark" aria-hidden="true">📚</span>
        LearnNow
      </router-link>

      <button
        class="navbar-toggler"
        type="button"
        aria-controls="mainNav"
        :aria-expanded="navOpen ? 'true' : 'false'"
        aria-label="Toggle navigation"
        @click="navOpen = !navOpen"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <div class="navbar-collapse" :class="navOpen ? 'show' : 'collapse'" id="mainNav">
        <ul class="navbar-nav mr-auto">
          <li class="nav-item">
            <router-link class="nav-link" to="/about" @click.native="closeNav">About</router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link" to="/contact" @click.native="closeNav">Contact</router-link>
          </li>
        </ul>

        <ul class="navbar-nav ml-auto align-items-lg-center" v-if="loggedIn">
          <!-- Verbal dropdown -->
          <li class="nav-item dropdown" :class="{ show: openDropdown === 'verbal' }">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              :aria-expanded="openDropdown === 'verbal' ? 'true' : 'false'"
              @click.prevent="toggleDropdown('verbal')"
            >Verbal</a>
            <div class="dropdown-menu dropdown-menu-right" :class="{ show: openDropdown === 'verbal' }">
              <router-link class="dropdown-item" to="/wordGenerator" @click.native="closeNav">Word Generator</router-link>
              <router-link class="dropdown-item" to="/missingLetter" @click.native="closeNav">Missing Letter</router-link>
              <router-link class="dropdown-item" to="/findWords" @click.native="closeNav">Find Words</router-link>
              <router-link class="dropdown-item" to="/wordSearch" @click.native="closeNav">Word Search</router-link>
            </div>
          </li>
          <!-- Quantitative dropdown -->
          <li class="nav-item dropdown" :class="{ show: openDropdown === 'quant' }">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              :aria-expanded="openDropdown === 'quant' ? 'true' : 'false'"
              @click.prevent="toggleDropdown('quant')"
            >Quantitative</a>
            <div class="dropdown-menu dropdown-menu-right" :class="{ show: openDropdown === 'quant' }">
              <router-link class="dropdown-item" to="/sortChallenge" @click.native="closeNav">Sort Challenge</router-link>
              <router-link class="dropdown-item" to="/operatorIdentifier" @click.native="closeNav">Operator Identifier</router-link>
              <router-link class="dropdown-item" to="/addMinus" @click.native="closeNav">Add &amp; Subtract</router-link>
              <router-link class="dropdown-item" to="/timesDivide" @click.native="closeNav">Multiply &amp; Divide</router-link>
              <router-link class="dropdown-item" to="/timesTablesTrainer" @click.native="closeNav">Times Tables Trainer</router-link>
              <router-link class="dropdown-item" to="/fractionsQuiz" @click.native="closeNav">Fractions Quiz</router-link>
              <router-link class="dropdown-item" to="/numberSequence" @click.native="closeNav">Number Sequence</router-link>
              <router-link class="dropdown-item" to="/shapeRecognition" @click.native="closeNav">Shape Recognition</router-link>
            </div>
          </li>
          <!-- Tools dropdown -->
          <li class="nav-item dropdown" :class="{ show: openDropdown === 'tools' }">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              :aria-expanded="openDropdown === 'tools' ? 'true' : 'false'"
              @click.prevent="toggleDropdown('tools')"
            >Tools</a>
            <div class="dropdown-menu dropdown-menu-right" :class="{ show: openDropdown === 'tools' }">
              <router-link class="dropdown-item" to="/standardCalc" @click.native="closeNav">Calculator</router-link>
            </div>
          </li>
          <!-- Colours dropdown -->
          <li class="nav-item dropdown" :class="{ show: openDropdown === 'colours' }">
            <a
              class="nav-link dropdown-toggle"
              href="#"
              role="button"
              :aria-expanded="openDropdown === 'colours' ? 'true' : 'false'"
              @click.prevent="toggleDropdown('colours')"
            >Colours</a>
            <div class="dropdown-menu dropdown-menu-right" :class="{ show: openDropdown === 'colours' }">
              <router-link class="dropdown-item" to="/colours" @click.native="closeNav">Colours Explorer</router-link>
            </div>
          </li>
          <!-- User / Profile dropdown -->
          <li class="nav-item dropdown user-menu" :class="{ show: openDropdown === 'user' }">
            <a
              class="nav-link dropdown-toggle user-badge"
              href="#"
              role="button"
              :aria-expanded="openDropdown === 'user' ? 'true' : 'false'"
              @click.prevent="toggleDropdown('user')"
            >
              <span class="user-avatar">{{ userInitial }}</span>
              <span class="user-email">Profile</span>
            </a>
            <div class="dropdown-menu dropdown-menu-right" :class="{ show: openDropdown === 'user' }">
              <router-link class="dropdown-item" to="/profile" @click.native="closeNav">Profile</router-link>
              <div class="dropdown-divider"></div>
              <a class="dropdown-item sign-out-item" href="#" @click.prevent="signOut">Sign Out</a>
            </div>
          </li>
        </ul>

        <ul class="navbar-nav ml-auto auth-nav align-items-lg-center" v-else>
          <li class="nav-item">
            <router-link class="nav-link nav-link--signin" to="/signin" @click.native="closeNav">Sign in</router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link nav-link--signup" to="/signup" @click.native="closeNav">Sign Up</router-link>
          </li>
        </ul>
      </div>
    </nav>
  </header>
</template>

<script>
import swal from "sweetalert";
import { auth } from "../firebase/firebase.utils";

export default {
  props: ["user"],

  data() {
    return {
      loggedIn: false,
      unsubscribeFromAuth: null,
      scrolled: false,
      navOpen: false,
      openDropdown: null,
    };
  },

  computed: {
    userInitial() {
      if (!this.user || !this.user.email) return "?";
      return this.user.email.charAt(0).toUpperCase();
    },
    userLabel() {
      if (!this.user || !this.user.email) return "Account";
      const email = this.user.email;
      return email.length > 18 ? email.substring(0, 16) + "…" : email;
    },
  },

  mounted() {
    this.unsubscribeFromAuth = auth.onAuthStateChanged((user) => {
      this.loggedIn = !!user;
    });
    this._onScroll = () => {
      this.scrolled = window.scrollY > 10;
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });
    this._onClickOutside = (e) => {
      if (this.navOpen && !this.$el.contains(e.target)) {
        this.closeNav();
      }
    };
    document.addEventListener("click", this._onClickOutside, true);
  },

  beforeDestroy() {
    this.unsubscribeFromAuth();
    window.removeEventListener("scroll", this._onScroll);
    document.removeEventListener("click", this._onClickOutside, true);
  },

  watch: {
    $route() {
      this.navOpen = false;
      this.openDropdown = null;
    },
  },

  methods: {
    toggleDropdown(name) {
      this.openDropdown = this.openDropdown === name ? null : name;
    },

    closeNav() {
      this.navOpen = false;
      this.openDropdown = null;
    },

    async signOut() {
      try {
        await auth.signOut();
        this.closeNav();
        await swal({
          title: "Thank you!",
          text: "You've successfully signed out!",
          icon: "success",
        });
        this.$router.replace({ name: "Home" });
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>

<style scoped>
.app-header {
  background: var(--color-surface);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;
  position: sticky;
  top: 1rem;
  z-index: 1050;
  transition: top 0.25s ease, border-radius 0.25s ease, box-shadow 0.25s ease;
}

/* When scrolled: snap flush to the viewport top, square off top corners */
.app-header--scrolled {
  top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.navbar {
  padding: 0.5rem 0.75rem;
}

.navbar-brand {
  font-family: var(--font-brand);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-primary-dark) !important;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.brand-mark {
  font-size: 1.35rem;
  line-height: 1;
}

.nav-link {
  font-weight: 600;
  color: var(--color-text) !important;
  padding: 0.5rem 0.85rem !important;
  border-radius: var(--radius-sm);
  transition: background 0.2s ease, color 0.2s ease;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: var(--color-primary-dark) !important;
  background: var(--color-primary-light);
}

.nav-link--signin {
  color: var(--color-primary-dark) !important;
}

.nav-link--signup {
  background: var(--color-primary) !important;
  color: #fff !important;
  margin-left: 0.25rem;
}

.nav-link--signup:hover {
  background: var(--color-primary-dark) !important;
  color: #fff !important;
}

.dropdown-menu {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  padding: 0.35rem 0;
  margin-top: 0.35rem;
  z-index: 1051;
}

.dropdown-item {
  font-weight: 600;
  color: var(--color-text);
  padding: 0.5rem 1.1rem;
  transition: background 0.15s ease;
}

.dropdown-item:hover {
  background: var(--color-primary-light);
  color: var(--color-primary-dark);
}

.user-badge {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--color-primary);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  flex-shrink: 0;
}

.user-email {
  font-size: 0.9rem;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sign-out-item {
  color: #dc2626 !important;
}

.sign-out-item:hover {
  background: #fef2f2 !important;
  color: #b91c1c !important;
}

.navbar-toggler {
  border-color: var(--color-border);
  padding: 0.35rem 0.6rem;
}

.navbar-toggler:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.25);
}

@media (max-width: 991px) {
  .auth-nav {
    padding-top: 0.5rem;
    padding-bottom: 0.75rem;
  }

  .nav-link--signup {
    display: inline-block;
    margin-top: 0.25rem;
  }

  .user-menu {
    margin-top: 0.5rem;
    border-top: 1px solid var(--color-border);
    padding-top: 0.5rem;
  }

  .dropdown-menu {
    border: none;
    box-shadow: none;
    background: transparent;
    padding-left: 0.5rem;
  }
}
</style>
